import { useEffect, useState } from 'react'

const QUERY = '(prefers-reduced-motion: reduce)'

/** True when the manual "Reduce motion" Settings toggle (F6) is on — the
 *  `data-reduce-motion` root attribute set by personalization.ts's
 *  applyPersonalization. The OS query is checked separately and still applies
 *  on top of this; see usePrefersReducedMotion below. */
function manualReduceMotion(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.reduceMotion === 'on'
}

/**
 * The same answer as the hook, without React: should motion be reduced right now?
 *
 * **Non-React code must call THIS, never `matchMedia` on its own.** Quil has two sources for the
 * preference — the OS query and the in-app Settings toggle (F6, for a device whose OS setting is
 * not the user's to change) — and a module that reads only the first ignores the switch Kamrin
 * actually flipped. `global.css`'s `:root[data-reduce-motion="on"] *` covers the CSS half, so the
 * gap is invisible until some JS decides to smooth-scroll or start a timer.
 *
 * Found by the P4 Bindr session on 2026-09-22, in the rail engine this file now serves.
 */
export function motionReduced(): boolean {
  if (manualReduceMotion()) return true
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(QUERY).matches
    : false
}

/**
 * Tracks whether motion should be reduced: either the OS "reduce motion"
 * preference, or Quil's own manual opt-in (F6, ported from Inkling — for a
 * device whose OS setting isn't the user's to change). Used to swap the loading
 * gate's animated ticker for its settled state (a decision we make in JS so
 * it's unit-testable, rather than relying solely on a CSS media query).
 */
export function usePrefersReducedMotion(): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  const [reduced, setReduced] = useState(
    () => manualReduceMotion() || (supported ? window.matchMedia(QUERY).matches : false),
  )

  useEffect(() => {
    if (!supported) return
    const mq = window.matchMedia(QUERY)
    const onChange = () => setReduced(manualReduceMotion() || mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [supported])

  return reduced
}
